import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import Pagination, { getStoredPageSize } from '../../../components/Pagination/Pagination';
import { processError } from '../../../services/ServiceUtils';
import { getErrorMessage } from '../../../utils/MessageUtils';
import { deleteStrategy, exportStrategySystem, getStrategies, startStrategy, stopStrategy, saveStrategy, labelStrategyRegime } from '../../../services/StrategiesService';
import { getItemsHealth } from '../../../services/BeholderService';
import MyStrategyRow from './MyStrategyRow';
import StrategyModal from '../StrategyModal/StrategyModal';
import ExportStrategyModal from '../ExportStrategyModal/ExportStrategyModal';
import SimulateStrategyModal from '../SimulateStrategyModal/SimulateStrategyModal';
import TestStrategyModal from '../TestStrategyModal/TestStrategyModal';
import ReapplyStrategyModal from '../ReapplyStrategyModal/ReapplyStrategyModal';
import MemeLayerAcceptModal from '../MemeLayerAcceptModal/MemeLayerAcceptModal';
import { findRowFromActionButton, getIdFromActionButton } from '../../../utils/DataRowButtonUtils';
import QuickSetupBanner from '../../../components/QuickSetupBanner/QuickSetupBanner';
import { getCurrentEnvironment, subscribeToCurrentEnvironment } from '../../../utils/CurrentEnvironmentStorage';
import { getCurrentMarket, subscribeToCurrentMarket } from '../../../utils/MarketStorage';
import OnlyActiveToggle from '../../../components/OnlyActiveToggle/OnlyActiveToggle';
import { getOnlyActive, setOnlyActive as storeOnlyActive } from '../../../utils/OnlyActiveStorage';

/**
 * props:
 * - symbol
 * - onNotification
 */
function MyStrategies(props) {

    const defaultLocation = useLocation();
    const history = useHistory();

    function getPage(location) {
        if (!location) location = defaultLocation;
        return new URLSearchParams(location.search).get('page');
    }

    function getSize(location) {
        if (!location) location = defaultLocation;
        return parseInt(new URLSearchParams(location.search).get('size')) || getStoredPageSize();
    }

    useEffect(() => {
        return history.listen(location => {
            setPage(getPage(location));
            setSize(getSize(location));
        });
    }, [history]); // eslint-disable-line react-hooks/exhaustive-deps

    const [count, setCount] = useState(0);
    const [page, setPage] = useState(getPage());
    const [size, setSize] = useState(getSize());
    const [strategies, setStrategies] = useState([]);
    const [healthMap, setHealthMap] = useState({});
    const [editStrategy, setEditStrategy] = useState({});
    const [exportStrategyData, setExportStrategyData] = useState({});
    const [simulateStrategyData, setSimulateStrategyData] = useState({});
    const [testStrategyData, setTestStrategyData] = useState({});
    const [reapplyStrategyData, setReapplyStrategyData] = useState({});
    const [memeGateData, setMemeGateData] = useState({}); // { id } quando o start devolve 422 memeGateRequired
    const [environment, setEnvironment] = useState(getCurrentEnvironment());
    const [market, setMarket] = useState(getCurrentMarket());
    const [onlyActive, setOnlyActiveState] = useState(getOnlyActive('strategies'));
    const [regimeLabelingId, setRegimeLabelingId] = useState(null); // estratégia sendo rotulada (spinner)
    const [orchestratingId, setOrchestratingId] = useState(null); // toggle de orquestração em voo (anti duplo-clique)

    useEffect(() => {
        return subscribeToCurrentEnvironment(setEnvironment);
    }, []);

    useEffect(() => {
        return subscribeToCurrentMarket(setMarket);
    }, []);

    useEffect(() => {
        getItemsHealth()
            .then(map => setHealthMap(map.strategies || {}))
            .catch(() => {});
    }, []);

    useEffect(() => {
        getStrategies(props.symbol, page || 1, size, environment, market, onlyActive)
            .then(result => {
                setStrategies(result.rows);
                setCount(result.count);
            })
            .catch(err => props.onNotification(getErrorMessage(processError(err))))
    }, [page, props.symbol, size, environment, market, onlyActive]); // eslint-disable-line react-hooks/exhaustive-deps

    function onToggleActive(value) {
        storeOnlyActive('strategies', value);
        setOnlyActiveState(value);
        const params = new URLSearchParams(defaultLocation.search);
        params.set('page', '1');
        history.push({ search: params.toString() });
    }

    function onEditClick(event) {
        setEditStrategy(findRowFromActionButton(event, strategies));
    }

    function onExportClick(event) {
        setExportStrategyData(findRowFromActionButton(event, strategies));
    }

    function onSimulateClick(event) {
        setSimulateStrategyData({ id: getIdFromActionButton(event) });
    }

    function onTestClick(event) {
        setTestStrategyData(findRowFromActionButton(event, strategies));
    }

    function onReapplyClick(event) {
        setReapplyStrategyData(findRowFromActionButton(event, strategies));
    }

    function onStopClick(event) {
        stopStrategy(getIdFromActionButton(event))
            .then(strategy => { history.go(0) })
            .catch(err => props.onNotification(getErrorMessage(processError(err))))
    }

    function onStartClick(event) {
        const id = getIdFromActionButton(event);
        startStrategy(id)
            .then(strategy => { history.go(0) })
            .catch(err => {
                // Camada MEME sem aceite de risco/orçamento — payload ESTRUTURADO (não string) com
                // memeGateRequired:true (Peça C.1 do plano). Abre o modal de aceite EM VEZ de mostrar
                // um erro genérico; não passa por processError (que desembrulha só .error e perderia
                // a flag). O modal re-chama startStrategy sozinho depois do aceite.
                if (err && err.response && err.response.data && err.response.data.memeGateRequired) {
                    setMemeGateData({ id });
                    return;
                }
                props.onNotification(getErrorMessage(processError(err)))
            })
    }

    function onDeleteClick(event) {
        deleteStrategy(getIdFromActionButton(event))
            .then(strategy => { history.go(0) })
            .catch(err => props.onNotification(getErrorMessage(processError(err))))
    }

    function onStrategySubmit(event) {
        history.go(0);
    }

    // Orquestrador por regime: liga/desliga o gate por-estratégia (patch regimeOrchestrated). O patch
    // REINICIA a estratégia se ela estiver ativa (stop+start dos componentes) — o guard de busy evita
    // que um duplo-clique dispare dois reinícios sobrepostos numa estratégia viva colocando ordens.
    function onToggleOrchestrateClick(event) {
        const strategy = findRowFromActionButton(event, strategies);
        if (!strategy || orchestratingId) return;
        setOrchestratingId(strategy.id);
        saveStrategy(strategy.id, { regimeOrchestrated: !strategy.regimeOrchestrated })
            .then(() => history.go(0))
            .catch(err => {
                setOrchestratingId(null);
                props.onNotification(getErrorMessage(processError(err)));
            });
    }

    // Rotula a estratégia pelo teste sintético (deriva os regimes ruins). Custa CPU; sem trade.
    function onLabelRegimeClick(event) {
        const id = getIdFromActionButton(event);
        setRegimeLabelingId(id);
        labelStrategyRegime(id)
            .then(() => { setRegimeLabelingId(null); history.go(0); })
            .catch(err => {
                setRegimeLabelingId(null);
                props.onNotification(getErrorMessage(processError(err)));
            });
    }

    async function onSystemExportClick(event) {
        const id = getIdFromActionButton(event);
        try {
            const response = await exportStrategySystem(id);
            const disposition = response.headers['content-disposition'] || '';
            const match = disposition.match(/filename="(.+)"/);
            const filename = match ? match[1] : `strategy-${id}.hydra.json`;
            const url = window.URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url; a.download = filename; a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            props.onNotification(getErrorMessage(processError(err)));
        }
    }

    return (
        <React.Fragment>
            {strategies && strategies.length === 0 && count === 0 && (
                <QuickSetupBanner onSuccess={() => history.go(0)} />
            )}
            <div className='d-flex justify-content-end mb-2'>
                <OnlyActiveToggle id='strategiesOnlyActive' checked={onlyActive} onChange={onToggleActive} />
            </div>
            <div className='card card-body border-0 shadow table-wrapper table-responsive'>
                <table className='table table-hover'>
                    <thead>
                        <tr>
                            <th className='border-gray-200'>Símbolo</th>
                            <th className='border-gray-200'>Nome</th>
                            <th className='border-gray-200'>Status</th>
                            <th className='border-gray-200'>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            strategies && strategies.length
                                ? strategies.map(strategy => (
                                    <MyStrategyRow key={strategy.id} data={strategy} health={healthMap[strategy.id]} onEditClick={onEditClick} onStopClick={onStopClick} onStartClick={onStartClick} onDeleteClick={onDeleteClick} onExportClick={onExportClick} onSystemExportClick={onSystemExportClick} onSimulateClick={onSimulateClick} onTestClick={onTestClick} onToggleOrchestrateClick={onToggleOrchestrateClick} onLabelRegimeClick={onLabelRegimeClick} onReapplyClick={onReapplyClick} regimeLabeling={regimeLabelingId === strategy.id} orchestrating={orchestratingId === strategy.id} />
                                ))
                                : <React.Fragment></React.Fragment>
                        }
                    </tbody>
                </table>
                <Pagination count={count} size={size} />
            </div>
            <StrategyModal data={editStrategy} onSubmit={onStrategySubmit} onUseAiFull={props.onUseAiFull} />
            <ExportStrategyModal data={exportStrategyData} onSuccess={() => history.go(0)} />
            <SimulateStrategyModal data={simulateStrategyData} />
            <TestStrategyModal data={testStrategyData} />
            <ReapplyStrategyModal data={reapplyStrategyData} onSuccess={() => history.go(0)} />
            <MemeLayerAcceptModal data={memeGateData} onSuccess={() => history.go(0)} />
        </React.Fragment>
    )
}

export default MyStrategies;
